<template>
  <teleport to="body">
    <div
      v-if="showModal"
      class="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50"
      aria-labelledby="auth-expired-title"
      aria-describedby="auth-expired-desc"
      role="dialog"
      aria-modal="true"
    >
      <div class="w-[92vw] max-w-md rounded-xl bg-white p-6 shadow-xl">
        <h2 id="auth-expired-title" class="mb-2 text-xl font-semibold">
          Sitzung abgelaufen
        </h2>

        <p id="auth-expired-desc" class="mb-4 text-sm text-gray-700">
          Deine Anmeldung ist nicht mehr gültig. Bitte melde dich erneut an, um fortzufahren.
        </p>

        <div v-if="secondsLeft !== null" class="mb-4 text-xs text-gray-500">
          (Verbleibende Sekunden: {{ secondsLeft }})
        </div>

        <div class="flex items-center gap-3">
          <button
          :style="{backgroundColor: 'var(--ghm-primary)', color: 'white'}"
            type="button"
            class="inline-flex w-full items-center justify-center rounded-lg border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            @click="goToLogin"
          >
            Zum Login
          </button>

          <button
            type="button"
            class="inline-flex w-full items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            @click="reload"
          >
            Seite neu laden
          </button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useAuthStore } from '@/stores/auth.store'
import { decodeJwtPayload } from '@/lib/utils'

type Props = {
  /** Prüfintervall in ms */
  checkEvery?: number
  /**
   * Sekunden vor Ablauf, ab denen das Modal optional schon angezeigt wird.
   * Standard=0 => Nur bei echtem Ablauf.
   */
  warnBeforeSeconds?: number
  /** Pfade, auf denen KEIN erzwungener Login/Modal stattfinden soll */
  skipPaths?: string[]
}

const props = withDefaults(defineProps<Props>(), {
  checkEvery: 30_000,
  warnBeforeSeconds: 0,
  skipPaths: () => ['/info/contact', '/info/support']
})

const showModal = ref(false)
const secondsLeft = ref<number | null>(null)
const isInitialLoad = ref(true)
let timer: number | undefined

const isSkippablePath = computed(() =>
  props.skipPaths?.some((p) => p === window.location.pathname)
)

/** Robust: JWT Body lesen (oder null) */
function safeDecodeJwtBody(token: string): Record<string, any> | null {
  try {
    const body = decodeJwtPayload(token)
    if (typeof body === 'object' && body) return body
    return null
  } catch {
    return null
  }
}

function calcSecondsLeftFromStorage(): number | null {
  const token = localStorage.getItem('accessToken')
  if (!token) return null
  const body = safeDecodeJwtBody(token)
  //console.log("decoded JWT body:", body)
  if (!body?.exp) return null
  const expMs = body.exp * 1000
  return Math.floor((expMs - Date.now()) / 1000)
}

function updateSecondsLeft() {
  secondsLeft.value = calcSecondsLeftFromStorage()
}

function maybeOpenModal() {
  // Auf Skip-Pfaden nie stören
  if (isSkippablePath.value) {
    showModal.value = false
    return
  }

  // Don't show modal on initial load to avoid flash when token is being set from URL
  if (isInitialLoad.value) {
    showModal.value = false
    isInitialLoad.value = false
    return
  }

  // Wenn wir keine Info haben (kein Token oder ungültig), Modal anzeigen
  if (secondsLeft.value === null) {
    showModal.value = true
    return
  }

  // Abgelaufen => Modal
  if (secondsLeft.value <= 0) {
    showModal.value = true
    return
  }

  // Optional: frühzeitig warnen
  if (props.warnBeforeSeconds > 0 && secondsLeft.value <= props.warnBeforeSeconds) {
    showModal.value = true
    return
  }

  // sonst geschlossen lassen
  showModal.value = false
}

function tick() {
  updateSecondsLeft()
  maybeOpenModal()
}

/** Login wie im Store-Flow: /api/auth/login?redirect=<aktuellerPfad+Query+Hash> */
function goToLogin() {
  const { pathname, search, hash } = window.location
  const redirect = encodeURIComponent(`${pathname}${search}${hash}`)
  window.location.href = `/api/auth/login?redirect=${redirect}`
}

function reload() {
  window.location.reload()
}

/** Reaktion auf Token-Änderungen in anderen Tabs/Fenstern */
function onStorage(e: StorageEvent) {
  if (e.key === 'accessToken') {
    tick()
  }
}

function start() {
  stop()
  window.addEventListener('storage', onStorage)
  timer = window.setInterval(tick, props.checkEvery)
  // initial
  tick()
}

function stop() {
  if (timer) {
    clearInterval(timer)
    timer = undefined
  }
  window.removeEventListener('storage', onStorage)
}

onMounted(start)
onUnmounted(stop)
</script>
